import { Component, h, Prop, State, Watch } from '@stencil/core';

@Component({
  tag: 'vs-content-card-grid',
  styleUrl: 'vs-content-card-grid.scss',
  shadow: false,
})
export class ContentCardGrid {
  @Prop() headline: string;
  @Prop() description: string;
  @Prop() tabsData: string;
  @Prop() loadMoreText: string;
  @Prop() initialTabVisible: number;
  @Prop() alignment: string;
  @Prop() theme: 'light' | 'dark' = 'light';
  // Video props removed as per requirement
  @Prop() visibleCards: number = 4;
  @Prop() titleBlock: string;

  @Watch('alignment')
  validateAlignment(newValue: string) {
    if (newValue !== 'left' && newValue !== 'center' && newValue !== 'right') {
      this.alignment = 'center';
    }
  }

  @Prop() cardsData: string;
  @Prop() showViewAll: boolean;
  @Prop() viewAllText: string;
  @Prop() viewAllLink: string;

  @State() cardParsedData: any = null;
  @State() activeTabIndex: number = 0;
  @State() windowWidth: number = 0;
  @State() tabStartIndex: number = 0;
  @State() showCloseButton: boolean = false;
  @State() titleBlockParsed: any = {};

  get totalCardCount(): number {
    const tabs = this.parsedTabs;
    const cards = this.cardParsedData || {};
    const activeTab = tabs[this.activeTabIndex];
    const tabData = cards[activeTab]?.cardInfo || [];

    let count = Math.min(this.visibleCards, tabData.length);
    if (this.showViewAll) {
      count++;
    }
    return count;
  }

  get parsedTabs(): string[] {
    if (Array.isArray(this.tabsData)) {
      return this.tabsData;
    }
    if (typeof this.tabsData === 'string') {
      try {
        return JSON.parse(this.tabsData);
      } catch (e) {
        console.warn('Could not parse tabs prop:', this.tabsData, e);
        return [];
      }
    }
    return [];
  }

  @Watch('cardsData')
  handleDataChange(newValue: string) {
    try {
      this.cardParsedData = JSON.parse(newValue);
    } catch (err) {
      console.log('cardsData Error', err);
      this.cardParsedData = [null];
    }
  }

  componentWillLoad() {
    this.windowWidth = window.innerWidth;
    this.handleDataChange(this.cardsData);
    this.titleBlockParsed = JSON.parse(this.titleBlock);
    window.addEventListener('resize', this.handleResize.bind(this));
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.handleResize.bind(this));
  }

  private handleResize() {
    this.windowWidth = window.innerWidth;
  }

  private handleTabClick(index: number) {
    this.activeTabIndex = index;
  }

  private handleCompareClicked(card, idx) {
    console.log('Compare clicked for card:', card, 'at index', idx);
  }

  private handleLoadMore(tabData) {
    this.visibleCards = Math.min(this.visibleCards + this.visibleCards, tabData.length);
  }

  private toggleTabs() {
    if (this.showCloseButton) {
      this.tabStartIndex = 0;
      this.activeTabIndex = 0;
      this.showCloseButton = false;
    } else {
      this.tabStartIndex = 0;
      this.showCloseButton = true;
    }
  }

  // Background video rendering removed as per requirement

  render() {
    const tabs = this.parsedTabs;
    const cards = this.cardParsedData || {};
    const activeTab = tabs[this.activeTabIndex];
    const gridVariant = cards[activeTab]?.gridVariant || 'normal';
    const cardVariant = cards[activeTab]?.cardVariant || 'default';
    const borderRadius = cards[activeTab]?.borderRadius || null;
    const animation = cards[activeTab]?.animation || null;
    const imageVariant = cards[activeTab]?.imageVariant || 'variant1';
    const imageAspectRatio = cards[activeTab]?.imageAspectRatio || null;
    const twoUpAR = cards[activeTab]?.twoUpAR || null;
    const tabData = cards[activeTab]?.cardInfo || [];
    const themeClass = `vs-content-card-grid-theme-${this.theme}`;
    const { variant, alignment } = this.titleBlockParsed;
    const variantAlignmentString = variant && alignment ? `${variant}-${alignment}` : '';

    if (!cards || Object.keys(cards).length === 0) {
      return (
        <h2 aria-live="assertive" role="alert">
          {activeTab ? `${activeTab}` : ''} Cards Data Not Found
        </h2>
      );
    }

    if (cards && Object.keys(cards).length === 0)
      return (
        <h2 aria-live="assertive" role="alert">
          {activeTab ? `${activeTab}` : ''} Cards Data Not Found
        </h2>
      );

    let displayedCards = tabData.slice(0, this.visibleCards);

    if (this.showViewAll) {
      displayedCards = [
        ...displayedCards,
        {
          isViewAll: true,
          viewAllText: this.viewAllText,
          viewAllLink: this.viewAllLink,
        },
      ];
    }

    return (
      <div
        class={`vs-content-card_content-card-grid ${themeClass}`}
        role="region"
        aria-label={this.headline}
      >
        <div class={`vsr-title-block_container ${variantAlignmentString}`}>
          <vsr-title
            title-block={this.titleBlock}
            is-padding-horizontal="false"
            comp="card-grid"
            theme={this.theme}
          ></vsr-title>
        </div>
        {tabs.length > 1 && this.initialTabVisible > 0 && (
          <vsr-tabs
            tabs={tabs}
            activeTabIndex={this.activeTabIndex}
            initialTabVisible={this.initialTabVisible}
            showCloseButton={this.showCloseButton}
            tabStartIndex={this.tabStartIndex}
            theme={this.theme}
            onTabClicked={e => this.handleTabClick(e.detail)}
            onTabToggleClicked={() => this.toggleTabs()}
            controlsId={`grid-${this.activeTabIndex}`}
          />
        )}
        {gridVariant === '2up' && tabData.length >= 2 ? (
          <vs-card-2up
            cards={tabData}
            borderRadius={borderRadius}
            animation={animation}
            imageVariant={imageVariant}
            imageAspectRatio={imageAspectRatio}
            cardVariant={cardVariant}
            twoUpAR={twoUpAR}
            theme={this.theme}
            onCompareClicked={(e: CustomEvent) =>
              this.handleCompareClicked(e.detail.card, e.detail.index)
            }
          />
        ) : gridVariant === '3up' && tabData.length >= 3 ? (
          <vs-card-3up
            cards={tabData}
            borderRadius={borderRadius}
            animation={animation}
            imageVariant={imageVariant}
            imageAspectRatio={imageAspectRatio}
            cardVariant={cardVariant}
            theme={this.theme}
            onCompareClicked={(e: CustomEvent) =>
              this.handleCompareClicked(e.detail.card, e.detail.index)
            }
          />
        ) : (
          <div
            id={`grid-${this.activeTabIndex}`}
            class={`vs-content-card__card-grid vs-content-card_alignment-${this.alignment} ${this.totalCardCount < 4 ? 'cards-center' : 'cards-grid'}`}
            role="grid"
            aria-label={activeTab}
          >
            {displayedCards.map((card, idx) =>
              card.isViewAll ? (
                <div
                  class={`vs-content-card__${cardVariant == 'product' ? 'product_' : ''}view-all-card ${imageVariant} ${borderRadius == 'Yes' ? 'b-radius' : ''} ${imageAspectRatio ? `aspect-ratio-${imageAspectRatio.replace(':', '-')}` : ''}`}
                  key="view-all"
                  role="gridcell"
                >
                  <a
                    href={card.viewAllLink}
                    class="vs-content-card__view-all-link"
                    target="_blank"
                    rel="noopener"
                    aria-label={card.viewAllText}
                  >
                    {card.viewAllText || 'View All'}
                    <span class="arrow-right"></span>
                  </a>
                </div>
              ) : (
                <div role="gridcell">
                  <vsr-card
                    type={card.type}
                    image-url={card.imageUrl}
                    borderRadius={borderRadius}
                    animation={animation}
                    imageVariant={imageVariant}
                    imageAspectRatio={imageAspectRatio}
                    cardVariant={cardVariant}
                    cardLink={card.cardLink}
                    cardLinkTarget={card.cardLinkTarget}
                    imageAltText={card.imageAltText}
                    title={card?.title || ''}
                    description={card?.description || ''}
                    cardCta={card.cardCta}
                    button-text-2={card.buttonText2}
                    key={`${card.title}+${card.idx}`}
                    onCompareClicked={() => this.handleCompareClicked(card, idx)}
                    theme={this.theme}
                    poster={card.imageUrl}
                    media-type={
                      card.mediaType == 'image'
                        ? 'image'
                        : card.mediaType == 'ambient-video-static'
                          ? 'single'
                          : 'loop'
                    }
                    video-alt={card.imageAltText}
                    media-src={card.videoUrl}
                  ></vsr-card>
                </div>
              )
            )}
          </div>
        )}
        {this.visibleCards < tabData.length && (
          <div class="vs-content-card_load-more-container">
            <button
              class="vs-content-card__load-more-button"
              aria-label={this.loadMoreText}
              aria-controls={`grid-${this.activeTabIndex}`}
              aria-expanded={this.visibleCards >= tabData.length ? 'true' : 'false'}
              onClick={() => this.handleLoadMore(tabData)}
            >
              {this.loadMoreText}
            </button>
          </div>
        )}
        <div aria-live="polite" class="sr-only">
          {this.visibleCards} of {tabData.length} {activeTab}
        </div>
      </div>
    );
  }
}
